import { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle, type ReactNode } from 'react';
import { usePhoneLayout } from './videoEditor/usePhoneLayout';
import { PHONE_INLINE_VIDEO } from './videoEditor/phoneInlineVideo';

export interface VideoEditorPlayerRef {
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  setPlaybackRate: (rate: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getVideoElement: () => HTMLVideoElement | null;
  getVideoDimensions: () => { width: number; height: number };
}

interface VideoEditorPlayerProps {
  videoUrl: string;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationChange?: (duration: number) => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onTogglePlay?: () => void;
  audioMuted?: boolean;
  className?: string;
  overlay?: ReactNode; // Canvas overlay for annotations
  /**
   * When true the player fills its parent's height (flex-1 min-h-0) and uses
   * object-fit:contain so the video scales dynamically. When false (default)
   * the player sizes by intrinsic aspect-ratio up to MAX_HEIGHT.
   */
  fillHeight?: boolean;
  /** Visual rotation applied to the video element (0 | 90 | 180 | 270). Default: 0. */
  rotation?: number;
  /** The source's own dimensions, once metadata has them. The phone stage is sized from these. */
  onVideoSize?: (width: number, height: number) => void;
  /**
   * Phone pinch zoom (spec 9.7): when given, the video sits in this layer,
   * which the zoom transforms. The play controls stay outside it. Desktop
   * passes nothing and renders as before.
   */
  zoomWrapRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * Phone landscape (spec 13.14): no tap-to-play targets. A tap on the video
   * there shows or hides the controls and never changes playback; the
   * editor draws its own play button. Off by default, so desktop is as before.
   */
  noTapTargets?: boolean;
}

export const VideoEditorPlayer = forwardRef<VideoEditorPlayerRef, VideoEditorPlayerProps>(
  ({ videoUrl, onTimeUpdate, onDurationChange, onPlayStateChange, onTogglePlay, audioMuted = false, className = '', overlay, fillHeight = false, rotation = 0, onVideoSize, zoomWrapRef, noTapTargets = false }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const isMobile = usePhoneLayout().phone;
    const rafRef = useRef<number | null>(null);
    const lastReportedTimeRef = useRef<number>(-1);
    // Smooth scrubbing: only issue the next video.currentTime seek after the
    // browser fires "seeked" (meaning the previous frame has fully decoded and
    // is on-screen).  Any intermediate positions are collapsed into the latest
    // pending value so the playhead still tracks in real time.
    const isSeekingRef = useRef(false);
    const pendingSeekTimeRef = useRef<number | null>(null);
    /**
     * Phone: decode the first frame without a tap. iOS loads only metadata
     * until something plays, so the picture stays black and every seek waits
     * forever for a `seeked` that cannot come, which froze the seek queue
     * above: the scrubber's time moved and the picture did not. A muted play()
     * is allowed without a gesture; pausing it once it starts leaves the frame
     * painted and lets pending seeks land. The play and pause it causes are
     * kept from the parent's play state, and it does not pause a play the
     * coach asked for meanwhile.
     */
    const primingRef = useRef(false);
    const wantsPlayRef = useRef(false);
    const isMobileRef = useRef(isMobile);
    const audioMutedRef = useRef(audioMuted);
    useEffect(() => { isMobileRef.current = isMobile; audioMutedRef.current = audioMuted; });
    const primeFirstFrame = () => {
      const v = videoRef.current;
      if (!isMobileRef.current || !v || v.readyState >= 2 || !v.paused || primingRef.current) return;
      primingRef.current = true;
      v.muted = true;
      const restore = () => { primingRef.current = false; v.muted = audioMutedRef.current; };
      v.play().then(() => { if (!wantsPlayRef.current) v.pause(); restore(); }).catch(restore);
    };
    const [isPlaying, setIsPlaying] = useState(false);
    const [containerMaxWidth, setContainerMaxWidth] = useState<number | null>(null);
    const [aspectRatio, setAspectRatio] = useState<string | undefined>(undefined);
    const MAX_HEIGHT = 600;

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const needsScale = rotation === 90 || rotation === 270;

    useEffect(() => {
      if (!needsScale) return;
      const el = containerRef.current;
      if (!el) return;
      const ro = new ResizeObserver(([entry]) => {
        setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
      });
      ro.observe(el);
      const { width, height } = el.getBoundingClientRect();
      if (width > 0) setContainerSize({ w: width, h: height });
      return () => ro.disconnect();
    }, [needsScale]);

    const rotationScale = needsScale && containerSize.w > 0 && containerSize.h > 0
      ? Math.min(containerSize.w, containerSize.h) / Math.max(containerSize.w, containerSize.h)
      : 1;

    // Cleanup rAF on unmount
    useEffect(() => {
      return () => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      };
    }, []);

    // Guard against loadedmetadata firing before React's event handler was
    // attached. When LessonVideoEditor is lazy-loaded, React renders it inside
    // Suspense's offscreen phase before committing it to the real DOM. The
    // browser starts fetching the video during that offscreen phase and can fire
    // loadedmetadata before commitMutationEffects registers the onLoadedMetadata
    // listener. Once committed, the event won't re-fire. This effect runs after
    // every mount (and videoUrl change) and immediately calls onDurationChange
    // if the video already has metadata — closing the gap.
    useEffect(() => {
      const video = videoRef.current;
      if (!video || video.readyState < HTMLMediaElement.HAVE_METADATA) return;
      if (onDurationChange) onDurationChange(video.duration);
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (vw && vh) {
        setAspectRatio(`${vw} / ${vh}`);
        onVideoSize?.(vw, vh);
        setContainerMaxWidth(vh > vw ? Math.round((vw / vh) * MAX_HEIGHT) : null);
      }
      primeFirstFrame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoUrl]); // runs on mount + URL changes; onDurationChange (setDuration) is a stable setter

    // When a seek completes, immediately start the next one if there's a
    // pending position queued up.  This chains seeks so frames always render
    // fully before advancing — eliminating the "flickering blank" jank from
    // issuing seeks faster than the browser can decode remote video frames.
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;
      const handleSeeked = () => {
        isSeekingRef.current = false;
        if (pendingSeekTimeRef.current !== null) {
          const t = pendingSeekTimeRef.current;
          pendingSeekTimeRef.current = null;
          video.currentTime = t;
          isSeekingRef.current = true;
        }
      };
      video.addEventListener('seeked', handleSeeked);
      return () => video.removeEventListener('seeked', handleSeeked);
    }, []);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      play: () => {
        wantsPlayRef.current = true;
        videoRef.current?.play().catch(() => { wantsPlayRef.current = false; });
      },
      pause: () => {
        wantsPlayRef.current = false;
        videoRef.current?.pause();
      },
      seek: (time: number) => {
        if (!videoRef.current) return;
        // Always update the playhead immediately for smooth tracking.
        lastReportedTimeRef.current = time;
        onTimeUpdate?.(time);
        // Gate the actual frame decode: if we're still waiting for the previous
        // seek to complete, just record the latest position.  The "seeked"
        // handler will pick it up and issue the real seek once the frame lands.
        if (isSeekingRef.current) {
          pendingSeekTimeRef.current = time;
          return;
        }
        videoRef.current.currentTime = time;
        isSeekingRef.current = true;
      },
      setPlaybackRate: (rate: number) => {
        if (videoRef.current) {
          videoRef.current.playbackRate = rate;
        }
      },
      getCurrentTime: () => {
        return videoRef.current?.currentTime || 0;
      },
      getDuration: () => {
        return videoRef.current?.duration || 0;
      },
      getVideoElement: () => {
        return videoRef.current;
      },
      getVideoDimensions: () => {
        if (videoRef.current) {
          return {
            width: videoRef.current.videoWidth || 640,
            height: videoRef.current.videoHeight || 480,
          };
        }
        return { width: 640, height: 480 };
      },
    }));

    // Simple audio muting using the video element's muted property
    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.muted = audioMuted;
      }
    }, [audioMuted]);

    // E2E test hook: allows headless tests to advance currentTime without a real
    // media pipeline. Calling window.__videoEditorForceTime(t) is equivalent to
    // the video firing a timeupdate at t, bypassing the rAF + event-dispatch chain.
    useEffect(() => {
      if (!onTimeUpdate) return;
      (window as unknown as Record<string, unknown>).__videoEditorForceTime = (t: number) => {
        lastReportedTimeRef.current = -1;
        onTimeUpdate(t);
      };
      return () => {
        delete (window as unknown as Record<string, unknown>).__videoEditorForceTime;
      };
    }, [onTimeUpdate]);

    // Handle time updates - throttled via rAF to avoid excessive re-renders.
    // Skips updates smaller than 0.05s to reduce renders during seeking.
    const handleTimeUpdate = useCallback(() => {
      if (!videoRef.current || !onTimeUpdate) return;
      if (rafRef.current !== null) return; // Already scheduled

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (!videoRef.current) return;
        const t = videoRef.current.currentTime;
        if (Math.abs(t - lastReportedTimeRef.current) < 0.05) return;
        lastReportedTimeRef.current = t;
        onTimeUpdate(t);
      });
    }, [onTimeUpdate]);

    // Handle duration loaded
    const handleLoadedMetadata = () => {
      if (videoRef.current) {
        if (onDurationChange) {
          onDurationChange(videoRef.current.duration);
        }
        const vw = videoRef.current.videoWidth;
        const vh = videoRef.current.videoHeight;
        if (vw && vh) {
          // Lock aspect ratio so the video element doesn't resize during seeks
          setAspectRatio(`${vw} / ${vh}`);
          onVideoSize?.(vw, vh);
          // Constrain container width for portrait videos
          if (vh > vw) {
            setContainerMaxWidth(Math.round((vw / vh) * MAX_HEIGHT));
          } else {
            setContainerMaxWidth(null);
          }
        }
      }
      primeFirstFrame();
    };

    // Handle play state changes
    const handlePlay = () => {
      if (primingRef.current) return;
      setIsPlaying(true);
      if (onPlayStateChange) {
        onPlayStateChange(true);
      }
    };

    const handlePause = () => {
      wantsPlayRef.current = false;
      setIsPlaying(false);
      if (onPlayStateChange) {
        onPlayStateChange(false);
      }
    };

    return (
      <div
        ref={containerRef}
        className={fillHeight
          ? `relative bg-black w-full h-full ${className}`
          : `relative bg-black mx-auto ${className}`}
        style={!fillHeight ? {
          ...(containerMaxWidth ? { maxWidth: `${containerMaxWidth}px` } : {}),
          ...(aspectRatio ? { aspectRatio, maxHeight: `${MAX_HEIGHT}px` } : {}),
        } : undefined}
      >
        {zoomWrapRef ? (
          <div ref={zoomWrapRef} className="absolute inset-0" style={{ willChange: 'transform' }}>
            <video
              ref={videoRef}
              src={videoUrl}
              /* On a phone, without playsinline Safari plays this in its own
                 fullscreen player, with its own controls over or instead of ours.
                 The overlay buttons below are the only controls; mobile only, so
                 desktop renders exactly as before. */
              {...(isMobile ? PHONE_INLINE_VIDEO : {})}
              className={fillHeight ? 'w-full h-full object-contain' : (aspectRatio ? 'w-full h-full' : 'w-full max-h-[600px] object-contain')}
              style={rotation ? { transform: `rotate(${rotation}deg)${rotationScale !== 1 ? ` scale(${rotationScale})` : ''}` } : undefined}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handlePause}
              crossOrigin="anonymous"
            />
          </div>
        ) : (
          <video
            ref={videoRef}
            src={videoUrl}
            /* On a phone, without playsinline Safari plays this in its own
               fullscreen player, with its own controls over or instead of ours.
               The overlay buttons below are the only controls; mobile only, so
               desktop renders exactly as before. */
            {...(isMobile ? PHONE_INLINE_VIDEO : {})}
            className={fillHeight ? 'w-full h-full object-contain' : (aspectRatio ? 'w-full h-full' : 'w-full max-h-[600px] object-contain')}
            style={rotation ? { transform: `rotate(${rotation}deg)${rotationScale !== 1 ? ` scale(${rotationScale})` : ''}` } : undefined}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handlePause}
            crossOrigin="anonymous"
          />
        )}

        {/* Canvas overlay for annotations */}
        {overlay && (
          <div className="absolute inset-0 pointer-events-auto">
            {overlay}
          </div>
        )}

        {/* Click target while playing — pauses on click, no visual overlay */}
        {isPlaying && !overlay && !noTapTargets && (
          <button
            className="absolute inset-0 w-full cursor-pointer bg-transparent"
            onClick={onTogglePlay}
            aria-label="Pause"
            type="button"
          />
        )}

        {/* Play button overlay when paused */}
        {!isPlaying && !overlay && !noTapTargets && (
          <button
            className="absolute inset-0 flex items-center justify-center w-full cursor-pointer"
            onClick={onTogglePlay}
            aria-label="Play"
            type="button"
          >
            <div className="bg-black/50 rounded-full pointer-events-none w-[12%] min-w-[40px] max-w-[96px] aspect-square flex items-center justify-center">
              <svg className="w-1/2 h-1/2 text-white ml-[8%]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </button>
        )}
      </div>
    );
  }
);

VideoEditorPlayer.displayName = 'VideoEditorPlayer';
